/**
 * whitelist.js
 *
 * Fetches a connected wallet's GTD merkle proof from the sharded static
 * files in public/whitelist/ (see contracts/scripts/generateMerkleTree.js
 * for why they're sharded rather than bundled).
 *
 * Only ONE shard is ever fetched — the one matching the first byte of the
 * connected address — and only after a wallet connects. Nothing is loaded
 * for visitors who never connect, and nothing lands in the JS bundle.
 *
 * Shards are immutable static files, so the result is cached in-memory for
 * the session; a repeat lookup for the same shard costs nothing.
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';

const shardCache = new Map(); // shardKey -> Promise<Record<address, proof[]>>
const addrCache = new Map();  // address -> Promise<proof[]|null>
let rootPromise = null;

const shardKeyFor = (address) => address.toLowerCase().slice(2, 4);

/** Collection-level whitelist metadata (root, count). */
export function fetchWhitelistRoot() {
  if (!rootPromise) {
    rootPromise = fetch(`${process.env.PUBLIC_URL || ''}/whitelist/root.json`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }
  return rootPromise;
}

function fetchShard(key) {
  if (!shardCache.has(key)) {
    const p = withTimeout(fetch(`${process.env.PUBLIC_URL || ''}/whitelist/${key}.json`), 8000)
      .then(async (r) => {
        // A missing shard is NORMAL — it just means no whitelisted
        // address starts with those two hex digits.
        //
        // The catch: single-page hosting (CRA's dev server, and Netlify
        // with an SPA rewrite) answers unknown paths with index.html and
        // status 200 rather than 404. So a missing shard arrives looking
        // like a success, and parsing that HTML as JSON throws — which
        // then reads as "check failed" when the truth is simply "not on
        // the list". Verify we actually got JSON before trusting it.
        if (r.status === 404) return {};

        const contentType = r.headers.get('content-type') || '';
        if (!r.ok || !contentType.includes('json')) return {};

        try {
          const body = await r.json();
          return body && typeof body === 'object' ? body : {};
        } catch {
          return {};   // served something that isn't JSON — treat as absent
        }
      })
      .catch((err) => {
        // Reaching here means the request itself failed (offline, DNS,
        // timeout) — genuinely "couldn't check", which is different from
        // "not listed". Don't cache it, so a blip isn't permanent.
        shardCache.delete(key);
        throw err;
      });
    shardCache.set(key, p);
  }
  return shardCache.get(key);
}

/**
 * @returns {Promise<string[]|null>} the merkle proof, or null if the
 *          address isn't on the list. Throws only on network failure,
 *          so callers can distinguish "not whitelisted" from "couldn't check".
 */
/**
 * Supabase lookup — the live path.
 *
 * Proofs live in a table so the whitelist can change without rebuilding
 * and redeploying the site. Returns undefined (not null) if the table
 * isn't reachable or doesn't exist, so the caller can tell "no such
 * address" apart from "couldn't ask".
 */
/** Reject rather than hang — a stalled lookup leaves the UI stuck on
 *  "checking" forever, which users read as broken. */
function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ]);
}

async function fromSupabase(address) {
  if (!isSupabaseConfigured) return undefined;   // fall straight to static files
  try {
    const { data, error } = await withTimeout(
      supabase
        .from('whitelist_proofs')
        .select('proof')
        .eq('wallet_address', address.toLowerCase())
        .maybeSingle(),
      6000
    );
    if (error) return undefined;                  // table missing / RLS — use fallback
    return data ? data.proof : null;
  } catch {
    return undefined;
  }
}

/**
 * @returns {Promise<string[]|null>} the merkle proof, or null if the
 *          address isn't on the list. Throws only when NEITHER source
 *          could be reached, so callers can distinguish "not
 *          whitelisted" from "couldn't check".
 *
 * Order: Supabase first (updatable without a deploy), then the static
 * shards baked into the build. Keeping the static fallback means a
 * Supabase outage during the mint degrades to whatever was shipped with
 * the last deploy rather than locking everyone out.
 */
export async function getProofFor(address) {
  if (!address) return null;
  const key = address.toLowerCase();
  if (addrCache.has(key)) return addrCache.get(key);

  const p = (async () => {
    const live = await fromSupabase(key);
    if (live !== undefined) return live;          // table answered
    const shard = await fetchShard(shardKeyFor(key));  // fall back to build
    return shard[key] || null;
  })().catch((err) => {
    addrCache.delete(key);   // don't cache a failure
    throw err;
  });

  addrCache.set(key, p);
  return p;
}
